import { createFileRoute } from "@tanstack/react-router";
import { Container } from "@/components/site/Container";
import { ButtonLink } from "@/components/site/Button";
import { businessNode, canonical } from "@/lib/seo";
import { ogImageMeta } from "@/lib/og-image";
import { Star, Phone } from "lucide-react";
import { REVIEWS, REVIEW_COUNT, REVIEW_RATING, GOOGLE_REVIEW_URL } from "@/lib/reviews";

const TITLE = "Durafoam Roofing Reviews | 4.8 Stars | Phoenix, AZ";
const DESCRIPTION =
  "Read real reviews from Phoenix homeowners who have trusted Durafoam Roofing since 1989. 4.8 stars on Google. Licensed ROC223900.";
const RATING = REVIEW_RATING;
const COUNT = REVIEW_COUNT;


function aggregateSchema() {
  return businessNode({
    url: "/reviews",
    aggregateRating: {
      ratingValue: RATING.toFixed(1),
      reviewCount: String(COUNT),
    },
    review: REVIEWS.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.author },
      reviewRating: { "@type": "Rating", ratingValue: String(r.stars), bestRating: "5" },
      reviewBody: r.body,
      datePublished: r.date,
    })),
  });
}

export const Route = createFileRoute("/reviews")({
  head: () => {
    const url = canonical("/reviews");
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        ...ogImageMeta("reviews"),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [{ type: "application/ld+json", children: JSON.stringify(aggregateSchema()) }],
    };
  },
  component: ReviewsPage,
});

function Stars({ value, size = 18 }: { value: number; size?: number }) {
  const pct = Math.max(0, Math.min(1, value / 5)) * 100;
  return (
    <span className="relative inline-block" style={{ width: size * 5, height: size }}>
      <span className="sr-only">{value} out of 5 stars</span>
      <span className="absolute inset-0 flex text-[#E0DDD6]" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="fill-current" style={{ width: size, height: size }} />)}
      </span>
      <span className="absolute inset-0 flex overflow-hidden text-accent" style={{ width: `${pct}%` }} aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="fill-current shrink-0" style={{ width: size, height: size }} />)}
      </span>
    </span>
  );
}

function ReviewsPage() {
  return (
    <>
      {/* Hero */}
      <section className="bg-primary py-16 text-white lg:py-20">
        <Container className="text-center">
          <h1 className="text-3xl font-bold sm:text-4xl lg:text-5xl">
            What Phoenix homeowners say about Durafoam Roofing
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-white/80">
            Honest roofing across the Phoenix Valley since 1989 — here is what our customers say.
          </p>
          <p className="mt-6 text-sm text-white/70">
            4.8 ★ Google rating · 84 verified reviews · Family-owned since 1989 · Licensed ROC223900
          </p>
        </Container>
      </section>

      {/* Aggregate */}
      <section className="bg-white py-14">
        <Container className="text-center">
          <div className="text-6xl font-bold text-primary">{RATING.toFixed(1)}</div>
          <div className="mt-3 flex justify-center"><Stars value={RATING} size={28} /></div>
          <p className="mt-3 text-sm text-[var(--muted-foreground)]">{COUNT} verified Google reviews</p>
          <div className="mt-6">
            <ButtonLink href={GOOGLE_REVIEW_URL} variant="primary" size="lg">
              See our reviews on Google
            </ButtonLink>
          </div>
        </Container>
      </section>

      {/* Grid */}
      <section className="bg-[var(--surface)] py-16">
        <Container>
          <div className="columns-1 gap-5 md:columns-2 lg:columns-3 [&>div]:mb-5 [&>div]:break-inside-avoid">
            {REVIEWS.map((r, i) => (
              <div
                key={i}
                className="rounded-xl border-[0.5px] border-[#E0DDD6] bg-white p-5 shadow-sm"
              >
                <Stars value={r.stars} size={16} />
                <p className="mt-3 text-[15px] leading-relaxed text-foreground/85">"{r.body}"</p>
                <p className="mt-4 text-xs text-[var(--muted-foreground)]">
                  {r.location ? `${r.location} · ` : ""}Verified Google review
                </p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Leave a review */}
      <section className="bg-white py-14">
        <Container className="text-center">
          <h2 className="text-2xl font-bold text-primary">Had a great experience with Durafoam?</h2>
          <p className="mt-3 text-[var(--muted-foreground)]">
            Leave us a review on Google — it helps other Phoenix homeowners find us.
          </p>
          <div className="mt-6">
            <ButtonLink href={GOOGLE_REVIEW_URL} variant="primary" size="lg">
              Leave a review
            </ButtonLink>
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="bg-primary py-14 text-white lg:py-16">
        <Container className="text-center">
          <h2 className="mx-auto max-w-3xl text-2xl font-bold text-white sm:text-3xl">
            Get your free roof inspection
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-white/80">
            Honest assessment, written estimate, no pressure.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <ButtonLink href="/free-roof-inspection-phoenix" variant="primary" size="lg">
              Schedule free inspection
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call 480-941-5373
            </ButtonLink>
          </div>
        </Container>
      </section>
    </>
  );
}
